/**
 * ====================================================================
 * FIKO CAD LINE STYLE FACTORY
 * ====================================================================
 *
 * Profesyonel CAD çizgi stilleri fabrikası
 * Dashed, dotted, center line, phantom vb.
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineStyleType, LineStyleConfig } from '../visual/DimensionVisualStyle';
import { getCanvasResolutionVec2 } from '../../../FikoCanvasResolution';

// ====================================================================
// DASH PATTERNS
// ====================================================================

/**
 * Önceden tanımlı dash pattern'leri
 */
export const DASH_PATTERNS: Record<LineStyleType, number[]> = {
    [LineStyleType.SOLID]: [],
    [LineStyleType.DASHED]: [4, 2],              // — — —
    [LineStyleType.DOTTED]: [1, 2],              // . . .
    [LineStyleType.DASH_DOT]: [4, 2, 1, 2],      // — . — .
    [LineStyleType.DASH_DOT_DOT]: [4, 2, 1, 2, 1, 2], // — . . — . .
    [LineStyleType.CENTER]: [8, 2, 2, 2],        // —— . —— .
    [LineStyleType.PHANTOM]: [8, 2, 2, 2, 2, 2], // —— . . —— . .
    [LineStyleType.CONSTRUCTION]: [6, 3],        // — — —
    [LineStyleType.HIDDEN]: [3, 2]               // - - -
};

// ====================================================================
// LINE STYLE FACTORY
// ====================================================================

/**
 * Line Style Factory - Profesyonel çizgi stilleri
 */
export class LineStyleFactory {
    // Material cache
    private static materialCache = new Map<string, THREE.Material>();

    // Pre-allocated buffers
    private static readonly _positions: number[] = [];

    /**
     * Cache'i temizle
     */
    static clearCache(): void {
        this.materialCache.forEach(material => material.dispose());
        this.materialCache.clear();
    }

    /**
     * Çizgi materyali oluştur
     */
    static createLineMaterial(config: LineStyleConfig, color: number): THREE.Material {
        const cacheKey = this.getMaterialCacheKey(config, color);

        if (this.materialCache.has(cacheKey)) {
            return this.materialCache.get(cacheKey)!;
        }

        let material: THREE.Material;

        if (config.type === LineStyleType.SOLID) {
            // Düz çizgi - Line2 ile kalın çizgi
            material = new LineMaterial({
                color,
                linewidth: config.width * 0.001, // Normalized
                transparent: config.opacity < 1,
                opacity: config.opacity,
                depthTest: false,
                depthWrite: false,
                resolution: getCanvasResolutionVec2()
            });
        } else {
            // Dash/dot pattern ile
            const dashPattern = config.dashPattern || DASH_PATTERNS[config.type] || [];
            const dashSize = dashPattern[0] || 1;
            const gapSize = dashPattern[1] || 1;

            material = new THREE.LineDashedMaterial({
                color,
                linewidth: config.width,
                dashSize: dashSize * config.width,
                gapSize: gapSize * config.width,
                transparent: config.opacity < 1,
                opacity: config.opacity,
                depthTest: false,
                depthWrite: false
            });
        }

        this.materialCache.set(cacheKey, material);
        return material;
    }

    /**
     * LineMaterial oluştur (kalın çizgiler için - Line2)
     */
    static createLine2Material(
        config: LineStyleConfig,
        color: number,
        resolution?: THREE.Vector2
    ): LineMaterial {
        const material = new LineMaterial({
            color,
            linewidth: config.width,
            transparent: config.opacity < 1,
            opacity: config.opacity,
            depthTest: false,
            depthWrite: false,
            resolution: resolution || getCanvasResolutionVec2(),
            dashed: config.type !== LineStyleType.SOLID,
            dashScale: 1,
            dashSize: (config.dashPattern?.[0] || 4) * config.width,
            gapSize: (config.dashPattern?.[1] || 2) * config.width
        });

        return material;
    }

    /**
     * Material cache key
     */
    private static getMaterialCacheKey(config: LineStyleConfig, color: number): string {
        return `${config.type}_${config.width}_${color}_${config.opacity}_${(config.dashPattern || []).join(',')}`;
    }

    /**
     * Line2 geometry oluştur (kalın çizgiler için)
     */
    static createLineGeometry(points: THREE.Vector3[]): LineGeometry {
        const geometry = new LineGeometry();

        const positions: number[] = [];
        for (const point of points) {
            positions.push(point.x, point.y, point.z);
        }

        geometry.setPositions(positions);
        return geometry;
    }

    /**
     * Standart THREE.Line geometry oluştur
     */
    static createBasicLineGeometry(points: THREE.Vector3[]): THREE.BufferGeometry {
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(points.length * 3);

        for (let i = 0; i < points.length; i++) {
            positions[i * 3] = points[i].x;
            positions[i * 3 + 1] = points[i].y;
            positions[i * 3 + 2] = points[i].z;
        }

        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        return geometry;
    }

    /**
     * Kalın çizgi oluştur (Line2)
     */
    static createLine2(
        points: THREE.Vector3[],
        config: LineStyleConfig,
        color: number
    ): Line2 {
        const geometry = this.createLineGeometry(points);
        const material = this.createLine2Material(config, color);

        const line = new Line2(geometry, material);
        line.computeLineDistances();

        return line;
    }

    /**
     * Standart çizgi oluştur
     */
    static createLine(
        points: THREE.Vector3[],
        config: LineStyleConfig,
        color: number
    ): THREE.Line {
        const geometry = this.createBasicLineGeometry(points);
        const material = this.createLineMaterial(config, color);

        const line = new THREE.Line(geometry, material);

        // Dashed ise line distances hesapla
        if (config.type !== LineStyleType.SOLID) {
            line.computeLineDistances();
        }

        return line;
    }

    /**
     * Gradient çizgi oluştur
     */
    static createGradientLine(
        points: THREE.Vector3[],
        config: LineStyleConfig,
        startColor: number,
        endColor: number
    ): THREE.Line {
        const geometry = this.createBasicLineGeometry(points);

        // Vertex colors
        const colors = new Float32Array(points.length * 3);
        const color1 = new THREE.Color(startColor);
        const color2 = new THREE.Color(endColor);
        const tempColor = new THREE.Color();

        for (let i = 0; i < points.length; i++) {
            const t = i / (points.length - 1);
            tempColor.lerpColors(color1, color2, t);
            colors[i * 3] = tempColor.r;
            colors[i * 3 + 1] = tempColor.g;
            colors[i * 3 + 2] = tempColor.b;
        }

        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const material = new THREE.LineBasicMaterial({
            vertexColors: true,
            linewidth: config.width,
            transparent: config.opacity < 1,
            opacity: config.opacity,
            depthTest: false,
            depthWrite: false
        });

        return new THREE.Line(geometry, material);
    }

    /**
     * Extension line oluştur (gap ve overshoot ile)
     */
    static createExtensionLine(
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        gap: number,
        overshoot: number,
        config: LineStyleConfig,
        color: number
    ): THREE.Line | Line2 {
        // Yön hesapla
        const direction = new THREE.Vector3()
            .subVectors(endPoint, startPoint)
            .normalize();

        // Gap uygula (başlangıçtan)
        const gapStart = startPoint.clone().addScaledVector(direction, gap);

        // Overshoot uygula (bitişten ötesi)
        const overshootEnd = endPoint.clone().addScaledVector(direction, overshoot);

        const points = [gapStart, overshootEnd];

        // Kalın çizgi gerekiyorsa Line2 kullan
        if (config.width > 1.5) {
            return this.createLine2(points, config, color);
        }

        return this.createLine(points, config, color);
    }

    /**
     * Dimension line oluştur (oklar arasındaki çizgi)
     */
    static createDimensionLine(
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        arrowSize: number,
        config: LineStyleConfig,
        color: number
    ): THREE.Line | Line2 {
        // Yön hesapla
        const direction = new THREE.Vector3()
            .subVectors(endPoint, startPoint)
            .normalize();

        // Ok boyutu kadar içeri al
        const lineStart = startPoint.clone().addScaledVector(direction, arrowSize * 0.2);
        const lineEnd = endPoint.clone().addScaledVector(direction, -arrowSize * 0.2);

        const points = [lineStart, lineEnd];

        if (config.width > 1.5) {
            return this.createLine2(points, config, color);
        }

        return this.createLine(points, config, color);
    }

    /**
     * Center mark oluştur (+ işareti)
     */
    static createCenterMark(
        center: THREE.Vector3,
        size: number,
        config: LineStyleConfig,
        color: number
    ): THREE.Group {
        const group = new THREE.Group();

        // Yatay çizgi
        const horizontal = this.createLine(
            [
                new THREE.Vector3(center.x - size, center.y, center.z),
                new THREE.Vector3(center.x + size, center.y, center.z)
            ],
            config,
            color
        );

        // Dikey çizgi
        const vertical = this.createLine(
            [
                new THREE.Vector3(center.x, center.y - size, center.z),
                new THREE.Vector3(center.x, center.y + size, center.z)
            ],
            config,
            color
        );

        group.add(horizontal, vertical);
        return group;
    }

    /**
     * Center line oluştur (uzun merkez çizgisi)
     */
    static createCenterLine(
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        overshoot: number,
        config: LineStyleConfig,
        color: number
    ): THREE.Line {
        // Yön hesapla
        const direction = new THREE.Vector3()
            .subVectors(endPoint, startPoint)
            .normalize();

        // Her iki uçtan overshoot kadar uzat
        const extendedStart = startPoint.clone().addScaledVector(direction, -overshoot);
        const extendedEnd = endPoint.clone().addScaledVector(direction, overshoot);

        const points = [extendedStart, extendedEnd];

        // Center line pattern'i zorla
        const centerConfig: LineStyleConfig = {
            ...config,
            type: LineStyleType.CENTER,
            dashPattern: DASH_PATTERNS[LineStyleType.CENTER]
        };

        return this.createLine(points, centerConfig, color);
    }

    /**
     * Oblique extension line oluştur (eğik)
     */
    static createObliqueExtensionLine(
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        obliqueAngle: number,
        gap: number,
        overshoot: number,
        config: LineStyleConfig,
        color: number
    ): THREE.Line {
        // Ana yön
        const direction = new THREE.Vector3()
            .subVectors(endPoint, startPoint)
            .normalize();

        // Eğiklik uygula
        const angleRad = (obliqueAngle * Math.PI) / 180;
        const rotationMatrix = new THREE.Matrix4().makeRotationZ(angleRad);

        const obliqueDir = direction.clone().applyMatrix4(rotationMatrix);

        // Noktaları hesapla
        const gapStart = startPoint.clone().addScaledVector(obliqueDir, gap);
        const overshootEnd = endPoint.clone().addScaledVector(obliqueDir, overshoot);

        return this.createLine([gapStart, overshootEnd], config, color);
    }

    /**
     * Construction line oluştur (sonsuz görünen)
     */
    static createConstructionLine(
        point: THREE.Vector3,
        direction: THREE.Vector3,
        length: number,
        config: LineStyleConfig,
        color: number
    ): THREE.Line {
        const halfLength = length / 2;

        const startPoint = point.clone().addScaledVector(direction, -halfLength);
        const endPoint = point.clone().addScaledVector(direction, halfLength);

        const constructionConfig: LineStyleConfig = {
            ...config,
            type: LineStyleType.CONSTRUCTION,
            dashPattern: DASH_PATTERNS[LineStyleType.CONSTRUCTION]
        };

        return this.createLine([startPoint, endPoint], constructionConfig, color);
    }

    /**
     * Resolution güncelle (window resize için)
     */
    static updateResolution(width: number, height: number): void {
        this.materialCache.forEach(material => {
            if (material instanceof LineMaterial) {
                material.resolution.set(width, height);
            }
        });
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

// DASH_PATTERNS zaten yukarıda export ediliyor
export { LineStyleType };
export type { LineStyleConfig };
